<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="mx-auto max-w-4xl">
      <div class="mb-6 flex flex-col gap-2">
        <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
          <Cog6ToothIcon class="h-4 w-4 text-primary" />
          Settings
        </div>
        <h2 class="text-3xl font-semibold text-gray-900">
          Profile Settings
        </h2>
        <p class="text-gray-600">
          Update your personal details and preferences.
        </p>
      </div>
      <nav
        aria-label="Breadcrumb"
        class="flex mb-3 justify-between"
      >
        <ol class="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li class="inline-flex items-center">
            <a
              class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-primary"
              href="#"
            >
              Dashboard
            </a>
          </li>
          <li>
            <div class="flex items-center">
              <svg
                aria-hidden="true"
                class="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                fill="none"
                viewBox="0 0 6 10"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m1 9 4-4-4-4"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
              <a
                class="ms-1 text-sm font-medium text-black hover:text-primary md:ms-2"
                href="#"
              >My Account</a>
            </div>
          </li>
          <li>
            <div class="flex items-center">
              <svg
                aria-hidden="true"
                class="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                fill="none"
                viewBox="0 0 6 10"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m1 9 4-4-4-4"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
              <a
                class="ms-1 text-sm font-medium text-black hover:text-primary md:ms-2"
                href="#"
              >Settings</a>
            </div>
          </li>
        </ol>
      </nav>
      <div class="p-6 bg-white border border-slate-200 shadow-sm rounded-2xl">
        <div class="flex justify-between">
          <h2 class="text-sm text-gray-800">
            Personal Details
          </h2>
        </div>
        <div class="flex items-center justify-between mt-2 mb-6">
          <div class="">
            <div class="text-xl text-gray-900">
              Profile photo
            </div>
            <div class="text-sm text-gray-500">
              Recommended size: 300 x 300
            </div>
          </div>
          <div>
            <a
              class="inline-flex justify-center items-center py-2 px-4 text-xs font-semibold text-gray-900 rounded-full bg-primary hover:bg-[#c9ad99]"
              href="#"
            >
              <svg
                class="mr-1 -ml-1 w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clip-rule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                  fill-rule="evenodd"
                />
              </svg>
              Upload Image
            </a>
          </div>
        </div>
        <div class="mb-6">
          <TextInput
            id="fullName"
            v-model="fullName"
            label="Full Name"
          />
        </div>
        <div class="mb-6">
          <TextInput
            id="company"
            v-model="company"
            label="Company Name"
          />
        </div>
        <div class="mb-6">
          <TextInput
            id="phoneNumber"
            v-model="phoneNumber"
            label="Phone Number"
          />
        </div>
        <div class="mb-6">
          <TextInput
            id="linkedinUrl"
            v-model="linkedinUrl"
            label="LinkedIn URL"
            placeholder="https://www.linkedin.com/in/username"
          />
        </div>
        <div class="mb-6">
          <TextInput
            id="xUrl"
            v-model="xUrl"
            label="X (Twitter) URL"
            placeholder="https://x.com/username"
          />
        </div>
        <div class="mb-6">
          <label
            class="block text-sm font-medium text-gray-700 mb-2"
            for="timeZone"
          >
            Time Zone
          </label>
          <select
            id="timeZone"
            v-model="timeZone"
            class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
          >
            <option value="">
              Select time zone
            </option>
            <option
              v-for="tz in timeZones"
              :key="tz"
              :value="tz"
            >
              {{ tz }}
            </option>
          </select>
        </div>
        <div class="border-t border-gray-200 pt-6">
          <div class="mb-2">
            <label class="flex items-center justify-between gap-4">
              <span class="text-sm font-medium text-gray-700">Show my profile in the member directory</span>
              <button
                class="relative inline-flex h-6 w-11 items-center rounded-full border border-gray-300 transition"
                :class="user!.profileVisibility ? 'bg-primary' : 'bg-white'"
                type="button"
                @click="user!.profileVisibility = !user!.profileVisibility"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                  :class="user!.profileVisibility ? 'translate-x-5 border border-[#c9ad99]' : 'translate-x-[2px] border border-gray-300'"
                />
              </button>
            </label>
          </div>
          <div class="mb-2">
            <label class="flex items-center justify-between gap-4">
              <span class="text-sm font-medium text-gray-700">Make my email public</span>
              <button
                class="relative inline-flex h-6 w-11 items-center rounded-full border border-gray-300 transition"
                :class="user!.emailVisibility ? 'bg-primary' : 'bg-white'"
                type="button"
                @click="user!.emailVisibility = !user!.emailVisibility"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                  :class="user!.emailVisibility ? 'translate-x-5 border border-[#c9ad99]' : 'translate-x-[2px] border border-gray-300'"
                />
              </button>
            </label>
          </div>
        </div>
        <div class="mt-8">
          <h2 class="text-md text-gray-800 mb-2">
            Notifications
          </h2>
          <div class="mb-2">
            <label class="flex items-center justify-between gap-4">
              <span class="text-sm font-medium text-gray-700">Send me email notifications</span>
              <button
                class="relative inline-flex h-6 w-11 items-center rounded-full border border-gray-300 transition"
                :class="user!.emailNotifications ? 'bg-primary' : 'bg-white'"
                type="button"
                @click="user!.emailNotifications = !user!.emailNotifications"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                  :class="user!.emailNotifications ? 'translate-x-5 border border-[#c9ad99]' : 'translate-x-[2px] border border-gray-300'"
                />
              </button>
            </label>
          </div>
          <div class="mb-2">
            <label class="flex items-center justify-between gap-4">
              <span class="text-sm font-medium text-gray-700">Notify me about new course content</span>
              <button
                class="relative inline-flex h-6 w-11 items-center rounded-full border border-gray-300 transition"
                :class="user!.courseContentNotifications ? 'bg-primary' : 'bg-white'"
                type="button"
                @click="user!.courseContentNotifications = !user!.courseContentNotifications"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition"
                  :class="user!.courseContentNotifications ? 'translate-x-5 border border-[#c9ad99]' : 'translate-x-[2px] border border-gray-300'"
                />
              </button>
            </label>
          </div>
        </div>
        <div class="border-t border-gray-200 pt-6 mt-8">
          <div class="mt-8">
            <h2 class="text-md text-gray-800 mb-2">
              Setup Preferences
            </h2>
            <div class="grid gap-6 md:grid-cols-2">
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-3">
                  Which VNTR offerings appeal to you? *
                </label>
                <div class="grid gap-3 md:grid-cols-2">
                  <label
                    v-for="option in offeringsOptions"
                    :key="option"
                    class="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      v-model="selectedOfferings"
                      class="h-4 w-4 rounded border-gray-300"
                      type="checkbox"
                      :value="option"
                    >
                    {{ option }}
                  </label>
                </div>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-3">
                  How would you describe yourself? *
                </label>
                <div class="flex flex-wrap gap-4">
                  <label
                    v-for="option in personaOptions"
                    :key="option"
                    class="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      v-model="persona"
                      class="h-4 w-4 border-gray-300"
                      type="radio"
                      :value="option"
                    >
                    {{ option }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-8">
          <h2 class="text-md text-gray-800 mb-2">
            Account Security
          </h2>
          <div class="grid gap-6 mb-6 md:grid-cols-2">
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-700"
                for="email"
              >Email</label>
              <input
                id="email"
                v-model="email"
                class="bg-white border border-gray-300 text-gray-900 text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                placeholder="john.doe@company.com"
                required
                type="email"
              >
            </div>
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-700"
                for="current-password"
              >Current
                Password</label>
              <input
                id="current-password"
                v-model="user!.password"
                class="bg-white border border-gray-300 text-gray-900 text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                placeholder="•••••••••"
                required
                type="password"
              >
            </div>
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-700"
                for="new-password"
              >New
                Password</label>
              <input
                id="new-password"
                v-model="user!.newPassword"
                class="bg-white border border-gray-300 text-gray-900 text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                placeholder="•••••••••"
                required
                type="password"
              >
            </div>
            <div>
              <label
                class="block mb-2 text-sm font-medium text-gray-700"
                for="confirm-password"
              >Confirm
                Password</label>
              <input
                id="confirm-password"
                v-model="user!.confirmPassword"
                class="bg-white border border-gray-300 text-gray-900 text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
                placeholder="•••••••••"
                required
                type="password"
              >
            </div>
          </div>
        </div>
        <div class="mt-8 flex flex-wrap items-center gap-3">
          <button
            class="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2 text-sm font-semibold text-black hover:bg-[#c9ad99]"
            type="button"
            :disabled="saving"
            @click="saveProfile"
          >
            Save Changes
          </button>
          <span
            v-if="saveMessage"
            class="text-sm text-gray-600"
          >
            {{ saveMessage }}
          </span>
        </div>
        <div class="mt-10 border-t border-gray-200 pt-6 grid">
          <h2 class="text-xl text-gray-900 mb-2 font-medium">
            Deactivate My Account
          </h2>
          <span class="text-gray-600 font-light">Warning: this action permanently deactivates your account and removes access to this community.</span>
          <a
            class="inline-flex justify-center items-center py-3 px-4 mt-3 w-52 text-xs font-medium text-center text-gray-900 rounded-full border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100"
            href="#"
          >
            <svg
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                fill-rule="evenodd"
              />
            </svg>
            Deactivate My Account
          </a>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, ref, watchEffect} from 'vue';
import {useRouter} from 'vue-router';
import axios from 'axios';
import TextInput from '@/components/TextInput.vue';
import {userKey} from "@/composables/useUser";
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {Cog6ToothIcon} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "Settings",
  components: {TextInput, Cog6ToothIcon},
  setup() {
    const {user} = inject(userKey)!;

    const {settings} = inject(settingsKey)!;
    const router = useRouter();
    const fullName = ref('');
    const company = ref('');
    const phoneNumber = ref('');
    const email = ref('');
    const linkedinUrl = ref('');
    const xUrl = ref('');
    const timeZone = ref('');
    const timeZones = ref<string[]>([]);
    const selectedOfferings = ref<string[]>([]);
    const persona = ref('');
    const saving = ref(false);
    const saveMessage = ref('');

    const offeringsOptions = [
      'Events for Investors',
      'Exclusive Deals',
      'Global Investor Community',
      'Curated VC Funds',
    ];

    const personaOptions = ['Investor', 'Sponsor', 'Startup'];

    const loadProfile = () => {
      fullName.value = user.value?.fullName || '';
      company.value = user.value?.company || '';
      phoneNumber.value = user.value?.phoneNumber || '';
      linkedinUrl.value = user.value?.linkedinUrl || '';
      xUrl.value = user.value?.xUrl || '';
      email.value = user.value?.email || '';
      timeZone.value = (user.value as { timeZone?: string } | undefined)?.timeZone || '';
      selectedOfferings.value = user.value?.offerings ? [...user.value.offerings] : [];
      persona.value = user.value?.persona || '';
    };

    const saveProfile = async () => {
      if (!user.value) return;
      saving.value = true;
      saveMessage.value = '';
      try {
        const response = await axios.put(`/user/update/${user.value._id}`, {
          fullName: fullName.value,
          company: company.value,
          phoneNumber: phoneNumber.value,
          linkedinUrl: linkedinUrl.value,
          xUrl: xUrl.value,
          email: email.value,
          timeZone: timeZone.value,
          offerings: selectedOfferings.value,
          persona: persona.value,
          setupCompleted: Boolean(selectedOfferings.value.length && persona.value),
        });
        user.value = response.data;
        saveMessage.value = 'Profile updated.';
      } catch (error) {
        console.error('Failed to update profile:', error);
        saveMessage.value = 'Failed to update profile.';
      } finally {
        saving.value = false;
      }
    };

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Profile Settings',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    watchEffect(() => {
      if (user.value) {
        loadProfile();
      }
    });

    const loadTimeZones = (): void => {
      if (typeof Intl !== 'undefined' && typeof (Intl as any).supportedValuesOf === 'function') {
        timeZones.value = (Intl as any).supportedValuesOf('timeZone');
      } else {
        timeZones.value = [
          'UTC',
          'Europe/London',
          'Europe/Istanbul',
          'Europe/Berlin',
          'America/New_York',
          'America/Los_Angeles',
        ];
      }
    };

    loadTimeZones();

    return {
      settings,
      user,
      router,
      fullName,
      company,
      phoneNumber,
      linkedinUrl,
      xUrl,
      email,
      timeZone,
      timeZones,
      offeringsOptions,
      personaOptions,
      selectedOfferings,
      persona,
      saving,
      saveMessage,
      saveProfile,
      Cog6ToothIcon,
    };
  },
});
</script>
